// @tennis-matches/ui/tournament-setup/PlayerInput
// Utility functions for player input management

import { __asStr } from "@tennis-matches/core/utils";

/**
 * Auto-fills gaps in player names object
 * Moves all filled names to consecutive positions starting at 1
 * ALLOWS duplicates - names can appear multiple times
 * @param {Object} namesObj - Object with numeric keys and name values
 * @param {number} minFields - Minimum number of fields to maintain
 * @returns {Object} New names object with gaps filled
 */
export function autoFillGaps(namesObj, minFields = 4) {
  if (!namesObj || typeof namesObj !== "object") {
    const filled = {};
    for (let i = 1; i <= minFields; i++) {
      filled[String(i)] = "";
    }
    return filled;
  }
  
  // Get all filled names (non-empty, not placeholder)
  const entries = Object.entries(namesObj)
    .map(([k, v]) => [Number(k), __asStr(v).trim()])
    .filter(([, v]) => v.length > 0 && v !== "(Optional)" && v !== "(optional)");

  // Sort by index
  entries.sort((a, b) => a[0] - b[0]);

  // Keep all entries (including duplicates)
  // Duplicates are now allowed

  // Build filled object with consecutive indices starting at 1
  const filled = {};
  entries.forEach(([, val], i) => {
    filled[String(i + 1)] = val;
  });

  // Ensure at least minFields entries exist
  const currentMax = Math.max(...Object.keys(filled).map(Number).filter(n => n > 0), 0);
  const targetMax = Math.max(minFields, currentMax);
  for (let i = 1; i <= targetMax; i++) {
    if (!filled[String(i)]) {
      filled[String(i)] = "";
    }
  }

  return filled;
}

/**
 * Finds duplicate names (case-insensitive)
 * @param {Object} namesObj - Object with numeric keys and name values
 * @returns {Set<string>} Set of field indices (as strings) that contain duplicate names
 */
export function findDuplicateFields(namesObj) {
  if (!namesObj || typeof namesObj !== "object") {
    return new Set();
  }

  // Count occurrences of each name (case-insensitive)
  const nameCounts = new Map();
  const fieldNames = new Map(); // Map field index to normalized name

  Object.entries(namesObj).forEach(([idx, name]) => {
    const normalizedName = __asStr(name).trim().toLowerCase();
    if (normalizedName.length > 0 && normalizedName !== "(optional)") {
      fieldNames.set(idx, normalizedName);
      nameCounts.set(normalizedName, (nameCounts.get(normalizedName) || 0) + 1);
    }
  });

  // Find fields with duplicate names
  const duplicateFields = new Set();
  fieldNames.forEach((normalizedName, idx) => {
    if (nameCounts.get(normalizedName) > 1) {
      duplicateFields.add(idx);
    }
  });

  return duplicateFields;
}

/**
 * Counts valid player names (non-empty, not placeholder)
 * @param {Object} namesObj - Object with numeric keys and name values
 * @returns {number} Count of valid player names
 */
export function countValidPlayers(namesObj) {
  if (!namesObj || typeof namesObj !== "object") return 0;
  return Object.values(namesObj).filter((name) => {
    const n = __asStr(name).trim();
    return n.length > 0 && n !== "(Optional)" && n !== "(optional)";
  }).length;
}

/**
 * Generates array of field indices to show
 * @param {Object} namesObj - Object with numeric keys and name values
 * @param {number} minFields - Minimum number of fields to show
 * @returns {number} Maximum field index to show
 */
export function getFieldsToShow(namesObj, minFields = 4) {
  if (!namesObj || typeof namesObj !== "object") {
    return minFields + 1;
  }
  
  const existingKeys = Object.keys(namesObj).map(Number).filter(k => k > 0);
  if (existingKeys.length === 0) {
    return minFields + 1;
  }
  
  const maxKey = Math.max(...existingKeys);
  return Math.max(minFields + 1, maxKey + 1);
}
